"use client";

import { useState } from "react";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { FloatingSticker } from "@/components/wallet/sticker-card-3d";
import { STICKER_REGISTRY } from "@/components/wallet/wallet-stickers";
import { useAuthState } from "@/hooks/use-auth-state";
import { useProfileStickers } from "@/hooks/use-stickers";
import { notificationLinks } from "@/lib/notification-links";
import { cn } from "@/lib/utils";
import { SharedStickerSheet } from "./shared-sticker-sheet";

type Filter = "all" | "common" | "theirs" | "mine";

const CARD_WIDTH = 128;
// FloatingSticker's stage: the card plus room for its float and shadow.
const RAIL_HEIGHT = Math.round(CARD_WIDTH * 1.4) + 32;

/**
 * The bottom of a profile: the member's Hiive stickers as live cards, and —
 * on someone else's profile — which ones you share. Tap a card to see it up
 * close and how close you are to it.
 */
export function ProfileStickersSection({
  userId,
  ownerName,
  isOwnProfile,
  className,
}: {
  userId: string;
  ownerName: string | null;
  isOwnProfile: boolean;
  className?: string;
}) {
  // Both collections in one call, so the "in common" line and the filters
  // arrive with the cards instead of pushing them down later.
  const stickers = useProfileStickers(userId);
  const [filter, setFilter] = useState<Filter>("all");
  const [sheet, setSheet] = useState<{ id: string; open: boolean } | null>(
    null,
  );
  const auth = useAuthState();
  const viewerId = auth.identity?.id ?? null;
  const loading = stickers.isPending;

  // A failed read hides the section rather than claiming "no stickers".
  if (stickers.isError) return null;
  const ownerIds = stickers.data?.ownerIds;

  const ownerSet = new Set(ownerIds ?? []);
  const viewerSet =
    !isOwnProfile && stickers.data?.viewerIds
      ? new Set(stickers.data.viewerIds)
      : null;
  const common = viewerSet
    ? STICKER_REGISTRY.filter(
        (sticker) => ownerSet.has(sticker.id) && viewerSet.has(sticker.id),
      )
    : [];
  const onlyTheirs = viewerSet
    ? STICKER_REGISTRY.filter(
        (sticker) => ownerSet.has(sticker.id) && !viewerSet.has(sticker.id),
      )
    : [];
  const onlyMine = viewerSet
    ? STICKER_REGISTRY.filter(
        (sticker) => viewerSet.has(sticker.id) && !ownerSet.has(sticker.id),
      )
    : [];
  const owned = STICKER_REGISTRY.filter((sticker) => ownerSet.has(sticker.id));
  const shown =
    filter === "common"
      ? common
      : filter === "theirs"
        ? onlyTheirs
        : filter === "mine"
          ? onlyMine
          : owned;
  const firstName = ownerName?.split(" ")[0] ?? "They";
  // Someone with no stickers yet keeps the section — a skeleton that then
  // vanishes reads as a glitch — as one quiet line instead of a rail.
  const emptyOther = !loading && !isOwnProfile && owned.length === 0;
  const filters: { value: Filter; label: string; count: number }[] = viewerSet
    ? [
        { value: "all", label: "All", count: owned.length },
        { value: "common", label: "In common", count: common.length },
        {
          value: "theirs",
          label: `Only ${firstName}`,
          count: onlyTheirs.length,
        },
        { value: "mine", label: "Only you", count: onlyMine.length },
      ]
    : [];

  return (
    <section
      aria-label="Hiive stickers"
      className={cn("flex flex-col space-y-3", className)}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
          Hiive stickers
        </h3>
        {loading ? (
          <span className="h-3.5 w-16 animate-pulse rounded-full bg-black/[0.06] dark:bg-white/[0.08]" />
        ) : (
          <span className="text-xs font-semibold text-text-secondary tabular-nums">
            {owned.length} of {STICKER_REGISTRY.length}
          </span>
        )}
      </div>

      {loading && !isOwnProfile && auth.authStatus === "authenticated" ? (
        // Same rows the comparison will fill: the line, then the chips.
        <div aria-hidden className="space-y-3">
          <div className="flex h-5 items-center">
            <div className="h-3.5 w-40 animate-pulse rounded-full bg-black/[0.06] dark:bg-white/[0.08]" />
          </div>
          <div className="flex gap-1.5">
            {[56, 92, 84, 78].map((width) => (
              <div
                key={width}
                className="h-[30px] animate-pulse rounded-full bg-black/[0.06] dark:bg-white/[0.08]"
                style={{ width }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {emptyOther ? (
        <p className="text-sm text-text-secondary">
          {firstName} hasn't unlocked a Hiive sticker yet.
          {viewerSet && viewerSet.size > 0
            ? ` You have ${viewerSet.size}.`
            : ""}
        </p>
      ) : null}

      {viewerSet && !loading && !emptyOther ? (
        <p className="text-sm text-text-secondary">
          {common.length > 0 ? (
            <>
              You both have{" "}
              <span className="font-semibold text-text-primary">
                {common.length}
              </span>{" "}
              {common.length === 1 ? "sticker" : "stickers"}
            </>
          ) : (
            "No stickers in common yet"
          )}
        </p>
      ) : null}

      {filters.length > 0 && !loading && !emptyOther ? (
        <div className="-mx-1 no-scrollbar flex gap-1.5 overflow-x-auto px-1">
          {filters.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150",
                filter === item.value
                  ? "border-text-primary bg-text-primary text-background"
                  : "border-border bg-surface text-text-secondary pointer-fine:hover:text-text-primary",
              )}
            >
              {item.label}{" "}
              <span className="tabular-nums opacity-70">{item.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      {emptyOther ? null : (
        // overflow-x-auto makes overflow-y auto too, so the cast shadow (20px
        // past the stage) needs room in the padding box or the rail scrolls
        // vertically; overflow-y-hidden keeps the 3D tilt from doing the same.
        <div
          className="-mx-3 no-scrollbar flex items-center overflow-x-auto overflow-y-hidden overscroll-x-contain px-1 pb-5"
          style={{ minHeight: RAIL_HEIGHT }}
        >
          {loading ? (
            Array.from({ length: 3 }, (_, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cards.
                key={index}
                className="mx-3 shrink-0 animate-pulse rounded-[16px] bg-black/[0.06] dark:bg-white/[0.07]"
                style={{
                  width: CARD_WIDTH,
                  height: Math.round(CARD_WIDTH * 1.4),
                }}
              />
            ))
          ) : shown.length === 0 ? (
            <div className="flex w-full flex-col items-center gap-2 px-3 text-center">
              <p className="text-sm text-text-secondary">
                {isOwnProfile
                  ? "Your first sticker is waiting in your wallet."
                  : filter === "mine"
                    ? `${firstName} has every sticker you have.`
                    : filter === "theirs"
                      ? `You have every sticker ${firstName} has.`
                      : "Nothing here yet."}
              </p>
              {isOwnProfile ? (
                <BackAwareLink
                  href={notificationLinks.walletStickers()}
                  className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold text-text-primary"
                >
                  See how to unlock one
                </BackAwareLink>
              ) : null}
            </div>
          ) : (
            shown.map((sticker, index) => (
              <button
                key={sticker.id}
                type="button"
                aria-label={`${sticker.title}. Open`}
                onClick={() => setSheet({ id: sticker.id, open: true })}
                className="shrink-0 rounded-3xl transition-transform duration-150 ease-out-strong focus-visible:outline-2 focus-visible:outline-brand active:scale-[0.98]"
              >
                <FloatingSticker
                  sticker={sticker}
                  width={CARD_WIDTH}
                  // ~2.7s apart: a third to two-thirds of each sway period, so
                  // neighbours always lean different ways.
                  phase={1.3 + index * 2.7}
                  footerLeft={
                    viewerSet?.has(sticker.id) && ownerSet.has(sticker.id)
                      ? "In common"
                      : undefined
                  }
                />
              </button>
            ))
          )}
        </div>
      )}

      {isOwnProfile && !loading && owned.length > 0 ? (
        <BackAwareLink
          href={notificationLinks.walletStickers()}
          className="w-fit text-xs font-semibold text-text-secondary underline-offset-2 hover:underline"
        >
          See all in your wallet
        </BackAwareLink>
      ) : null}

      {sheet ? (
        <SharedStickerSheet
          open={sheet.open}
          onOpenChange={(open) => setSheet({ ...sheet, open })}
          stickerId={sheet.id}
          viewerId={viewerId}
          owner={
            // "Only you" lists the viewer's own stickers.
            filter === "mine" && viewerId
              ? { id: viewerId, name: "You" }
              : { id: userId, name: ownerName ?? "This member" }
          }
        />
      ) : null}
    </section>
  );
}
